import * as React from "react";
import { toast } from "react-toastify";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import { useRouteMatch } from "react-router-dom";

import { sentenceCase } from "src/library/utils/string-manipulations";
import { TabbedNavigation } from "src/library/components/TabbedNavigation";
import { ITableInstance } from "src/library/components/Tables/Table";
import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { PageProps } from "src/pages/Router/routes";
import {
  usePaymentApi,
  IPaymentCard,
  PaymentCardUsage
} from "src/modules/payment/api/payment.api";
import { PaymentMethodCard } from "src/modules/payment/components/PaymentMethodCard";
import { configToS3BucketUrl } from "src/utils/aws/config-to-s3-bucket-url";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { IStore } from "src/core/shared/store";
import { ITransaction } from "src/modules/payment/api/transactions.api";
import {
  DeletePaymentMethodModal,
  TransactionsTab,
  AssignedDropsTab
} from "src/modules/payment/components";
import { useDropsApi } from "src/core/drops/api/drops.api";
import { useDrop } from "src/core/drops/hooks";
import { MakeCardDefaultModal } from "src/modules/payment/components/MakeCardDefaultModal";
import { HelperText } from "src/library/components/HelperText/HelperText";

interface PaymentMethodPageProps {
  paymentMethodId: string;
}

/**
 * Payment method page
 */
const PaymentMethodPage: React.ReactFCWithChildren<PageProps<PaymentMethodPageProps>> = props => {
  const { terminology } = props;
  const defaultTab = React.useMemo(() => props.location.hash.replace("#", ""), [ props.location.hash ]);
  const match = useRouteMatch<PaymentMethodPageProps>();
  const TableRef = React.useRef<ITableInstance<ITransaction>>(null);
  const paymentMethodId = React.useMemo(() => parseInt(match.params.paymentMethodId, 0), [ match ]);
  const [ paymentMethod, setPaymentMethod ] = React.useState<IPaymentCard>();
  const [ showMakeCardDefaultModal, setShowMakeCardDefaultModal ] = React.useState(false);
  const [ showDeletePaymentMethodModal, setShowDeletePaymentMethodModal ] = React.useState(false);
  const [ getPaymentMethodRes, getPaymentMethodReq ] = usePaymentApi("GET_PAYMENT_METHOD");
  const [ getUserDropsResponse, listUserDrops ] = useDropsApi("GET_USER_DROPS");
  const [ { drop }, { checkDropIsSynced } ] = useDrop();

  const { awsConfig, currentUser } = useMappedState(({ global }: IStore) => ({
    awsConfig: global.settings?.aws,
    currentUser: global.user
  }));

  const s3BucketUrl = React.useMemo(() => configToS3BucketUrl(awsConfig), [ awsConfig ]);

  // On mount
  React.useEffect(() => {
    // attempt to get the payment method via the API
    if (!paymentMethod) {
      getPaymentMethodReq(paymentMethodId);
    }

    // grab all drops so we can determine symantics in modal body
    if (drop && currentUser && !getUserDropsResponse.data) {
      listUserDrops({
        userId: currentUser.id,
        filters: {
          customerId: currentUser.customerId,
          status: "active"
        },
        pagination: {
          offset: 0,
          page: 0,
          limit: 1000,
          sort: [ [ "name", "ASC" ] ]
        }
      });
    }
  }, [
    currentUser,
    drop,
    getPaymentMethodReq,
    getUserDropsResponse.data,
    listUserDrops,
    paymentMethod,
    paymentMethodId
  ]);

  // On getPaymentMethodRes change
  React.useEffect(() => {
    // If an error occurs
    if (getPaymentMethodRes.error) {
      // Show the error
      toast.error(getPaymentMethodRes.error.message);
      // Push the user back to the payment overview tab
      props.history.push("/settings/user#payment_methods");
    }

    // On success
    if (getPaymentMethodRes.data) {
      // Update the paymentMethod state
      setPaymentMethod(getPaymentMethodRes.data);
    }
  }, [
    getPaymentMethodRes.data,
    getPaymentMethodRes.error,
    props.history
  ]);

  const availableTabs = React.useMemo(() => {
    const tabs = [];

    if (paymentMethod) {
      // only display assigned branches tab if customer has multiple drops
      if (getUserDropsResponse.data?.count && getUserDropsResponse.data.count > 1) {
        tabs.push({
          title: `Assigned ${sentenceCase(props.terminology.drop.plural.text)}`,
          name: props.terminology.drop.plural.text,
          content: () => (
            <AssignedDropsTab
              paymentMethod={paymentMethod}
              terminology={terminology}
            />
          )
        });
      }

      tabs.push({
        title: "Transactions",
        name: "transactions",
        content: () => (
          <TransactionsTab
            paymentMethod={paymentMethod}
            terminology={terminology}
          />
        )
      });
    }

    return tabs;
  }, [
    getUserDropsResponse.data?.count,
    paymentMethod,
    props.terminology.drop.plural.text,
    terminology
  ]);

  // handle updating relevant state when making card default successfully actioned
  const handleMakeCardDefaultSuccess = React.useCallback(() => {
    // update drop in redux to have updated default payment card id
    checkDropIsSynced();

    // reload table to get up-to-date data
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }

    // close modal
    setShowMakeCardDefaultModal(false);
  }, [ checkDropIsSynced ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <StyledPaymentMethodPage>
          <div className="payment-card-managment-wrapper">
            <PaymentMethodCard
              loading={getPaymentMethodRes.pending}
              s3BucketBaseUrl={s3BucketUrl}
              cardDetails={paymentMethod}
              backgroundColorType="light"
              hideDefaultLine
            />

            <div className="payment-card-managment-buttons-wrapper">

              <Button
                type="button"
                disabled={getPaymentMethodRes.pending || drop?.defaultPaymentCardId === paymentMethod?.id}
                onClick={() => setShowMakeCardDefaultModal(true)}
              >
                Set as default
              </Button>

              <Button
                type="button"
                disabled={getPaymentMethodRes.pending}
                onClick={() => {
                  // Error if the card has branches associated with it
                  if (paymentMethod?.branchPaymentCards && paymentMethod.branchPaymentCards.length > 0) {
                    toast.error(`This card cannot be deleted because it has ${props.terminology.drop.plural.text} associated with it. Please delete them and try again.`);

                    return;
                  }

                  setShowDeletePaymentMethodModal(true);
                }}
              >
                Delete
              </Button>

              {paymentMethod?.usage === PaymentCardUsage.ON_SESSION && (
                <div className="card-limitation">
                  <Icon
                    icon={Icons.INFO}
                    size={20}
                  />

                  <Text className="card-limitation-text">
                    This card can only be used for on-session transactions set by the card issuer. We will not be able to use the card for
                    {" "}

                    <HelperText
                      tipPlace="left"
                      tippyId="off-session-payments"
                      helpText="off sesion payments"
                      helpExplanation=" Off session payments happen in the background. We attempt to charge your card if you have not already paid for the order by the cutoff time. Using a payment method that allows off session payments will help you never miss an order."
                    />
                  </Text>
                </div>
              )}
            </div>
          </div>

          <TabbedNavigation
            defaultTab={defaultTab}
            className="tab-navigator"
            tabs={availableTabs}
          />
        </StyledPaymentMethodPage>

        {paymentMethod && (
          <>
            <MakeCardDefaultModal
              open={showMakeCardDefaultModal}
              onDismiss={() => setShowMakeCardDefaultModal(false)}
              paymentMethod={paymentMethod}
              terminology={terminology}
              drop={drop}
              dropsCount={getUserDropsResponse.data?.count || 0}
              onUpdateSuccess={handleMakeCardDefaultSuccess}
            />

            <DeletePaymentMethodModal
              open={showDeletePaymentMethodModal}
              onDismiss={() => setShowDeletePaymentMethodModal(false)}
              paymentMethod={paymentMethod}
              terminology={terminology}
            />
          </>

        )}
      </MainContentWrapper>
    </Layout>
  );
};

export default PaymentMethodPage;

const StyledPaymentMethodPage = styled.div`
  ${({ theme }) => css`
    .payment-card-managment-wrapper {
      display: flex;

      .payment-card-managment-buttons-wrapper {
        padding-top: 0.1rem;
        padding-left: 1.8rem;

        .card-limitation {
          display: flex;

          .card-limitation-text {
            padding-top: 0.2rem;
            padding-left: 0.8rem;

            .off-session-text {
              text-decoration: underline;
            }
          }
        }

        button {
          margin-bottom: ${theme.spacing.margins.md};
        }
      }
    }

    .tab-navigator {
      padding-top: 4.2rem;
    }
  `};
`;
